import { DestroyRef, ElementRef, inject, signal, Signal } from '@angular/core';

/**
 * Señal de "esta pantalla es la que se está viendo", alimentada por el ciclo de vida de
 * Ionic (`ionViewWillEnter` / `ionViewWillLeave`, eventos DOM en el elemento de la página).
 *
 * Va en el `enabled` de las queries de la pantalla: Ionic conserva montada la última página
 * de cada tab, así que sin esto una pantalla en segundo plano sigue con sus queries activas
 * y responde a cualquier `invalidateQueries()` — por ejemplo el del cambio de campus, que
 * hacía a la pantalla del boleto pedir `bustickets/{id}` estando el usuario en Perfil
 * (404, QA lo reportó dos veces en el PR #278). También corta el `refetchInterval` de las
 * pantallas con polling mientras nadie las ve.
 *
 * Se apaga en `WillLeave` a propósito, no en `DidLeave`: es el momento en que la vista aún
 * está enganchada al change detection, así que el `enabled` nuevo alcanza a llegar al
 * QueryObserver antes de que Ionic esconda la página y desenganche su ChangeDetectorRef
 * (una vista desenganchada ya no corre sus effects, y las opciones de `injectQuery` viajan
 * en uno).
 *
 * Al volver a entrar, la query se rehabilita y TanStack refetchea sola si quedó marcada
 * como stale — que es justo lo que se quiere después de un cambio de campus.
 *
 * Debe llamarse en contexto de inyección (inicializador de campo del componente).
 */
export function injectIsViewActive(): Signal<boolean> {
  const host = inject(ElementRef).nativeElement as HTMLElement;
  // Arranca en `true`: la página se crea porque se está entrando a ella, y el primer
  // `ionViewWillEnter` llega después de la construcción.
  const isActive = signal(true);

  const enter = () => isActive.set(true);
  const leave = () => isActive.set(false);

  host.addEventListener('ionViewWillEnter', enter);
  host.addEventListener('ionViewWillLeave', leave);

  inject(DestroyRef).onDestroy(() => {
    host.removeEventListener('ionViewWillEnter', enter);
    host.removeEventListener('ionViewWillLeave', leave);
  });

  return isActive.asReadonly();
}
